/**
 * The Roadmap's Timeline layout (D193 phase 2B), at both levels: one switch,
 * "What happened · What’s ahead".
 *
 *   What happened — the work timeline of the runs that served the roadmap
 *                   (RunEmbeds' RoadmapTimeline), as before;
 *   What’s ahead  — the dated roadmap, an honest Gantt drawn by the shared
 *                   renderer: finished work from its runs, work under way,
 *                   a person's target, and where there is none a forecast
 *                   from the recent pace — hatched and labelled as one —
 *                   with Today, the critical path, what waits on what and
 *                   what has no date yet. The line above it says how the
 *                   forecast was made. The Picture menu in the header saves
 *                   and exports this same picture.
 *
 * A bar or a name in the picture opens that item.
 *
 * Console: the picture is ./GanttChart.tsx (the renderer's date scale in React) rather than the
 * Diagrammer's DiagramPicture, so there is one look (Clean) and no Whiteboard switch; the Picture menu
 * (save and export) is the workbench's.
 */
import { Calendar, ChartBar } from '@gravity-ui/icons';
import { ListBox, Select } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import type { ReactNode } from 'react';

import { ArtEmpty } from '../ui/Art.tsx';
import { GanttChart } from './GanttChart.tsx';
import type { GanttGrounding } from './roadmapGantt.ts';
import { forecastLine, GANTT_LANES, type GanttLanes, TIMELINE_MODES, type TimelineMode } from './roadmapGanttModel.ts';

export function RoadmapTimelineSwitch({ mode, onMode }: { mode: TimelineMode; onMode: (mode: TimelineMode) => void }) {
  return (
    <Segment aria-label="What the timeline shows" size="sm" selectedKey={mode} onSelectionChange={(key) => onMode(String(key) === 'happened' ? 'happened' : 'ahead')} data-roadmap-timeline-mode={mode}>
      {TIMELINE_MODES.map((m) => (
        <Segment.Item key={m.id} id={m.id} aria-label={`${m.label}: ${m.hint}`}>
          {m.label}
        </Segment.Item>
      ))}
    </Segment>
  );
}

export function RoadmapTimelineArea({
  mode,
  onMode,
  lanes,
  onLanes,
  extra,
  gantt,
  happened,
  onOpen,
}: {
  mode: TimelineMode;
  onMode: (mode: TimelineMode) => void;
  lanes: GanttLanes;
  onLanes: (lanes: GanttLanes) => void;
  /** Console: beside the switch, the view's own control (which roadmap). */
  extra?: ReactNode;
  gantt: { grounding: GanttGrounding | undefined; loading: boolean; error?: string };
  /** The work timeline of the runs, as the layout showed before. */
  happened: ReactNode;
  /** A bar or a name in the picture was pressed: that item. */
  onOpen?: (id: string) => void;
}) {
  const g = gantt.grounding;
  const line = forecastLine(g);
  return (
    <div className="flex min-w-0 flex-col gap-3" data-roadmap-timeline-area={mode}>
      <div className="flex flex-wrap items-center gap-2">
        <RoadmapTimelineSwitch mode={mode} onMode={onMode} />
        <div className="flex-1" />
        {extra}
        {mode === 'ahead' ? (
          <>
            <Select className="w-48" variant="secondary" aria-label="Lanes" selectedKey={lanes} onSelectionChange={(key) => key !== null && onLanes(String(key) as GanttLanes)} data-roadmap-gantt-lanes>
              <Select.Trigger>
                <Select.Value />
                <Select.Indicator />
              </Select.Trigger>
              <Select.Popover>
                <ListBox>
                  {GANTT_LANES.map((l) => (
                    <ListBox.Item key={l.id} id={l.id} textValue={l.label}>
                      {l.label}
                      <ListBox.ItemIndicator />
                    </ListBox.Item>
                  ))}
                </ListBox>
              </Select.Popover>
            </Select>
          </>
        ) : null}
      </div>
      {mode === 'happened' ? (
        happened
      ) : gantt.loading && !g ? (
        <p className="m-0 py-10 text-center text-xs text-muted" aria-busy>
          Reading the work behind the roadmap…
        </p>
      ) : !g ? (
        <ArtEmpty compact tone="green" icon={<Calendar />} title="Nothing to put on dates yet">
          Add a target to an item, or hand one to a team: the work it gets is drawn here.
        </ArtEmpty>
      ) : (
        <>
          {line ? (
            <p className="m-0 flex items-start gap-2 text-xs leading-relaxed text-muted" title={line.full} data-roadmap-forecast={g.forecast.pace ? 'pace' : 'none'}>
              <ChartBar className="mt-0.5 size-3.5 shrink-0" />
              <span>
                {line.summary}
                {line.late ? <span className="text-warning"> {line.late}</span> : null}
                {gantt.error ? ` The runs could not be read (${gantt.error}), so no work is drawn.` : ''}
              </span>
            </p>
          ) : null}
          {/* Wide enough to read on a narrow window: it scrolls sideways there rather than shrink. */}
          <div className="ops-scroll overflow-x-auto rounded-2xl border border-separator/60 bg-surface/30">
            <div className="w-max min-w-full p-2" data-roadmap-gantt>
              <GanttChart spec={g.spec} {...(onOpen ? { onOpen } : {})} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
